import { DataNode } from 'components/WorkspaceEditor/types'
import { useEffect, useMemo, useRef } from 'react'

/**
 * Hook to automatically expand articles that are currently loading
 * @param articlesLoading - Array of article IDs currently loading
 * @param expandedKeys - Array of keys for currently expanded nodes
 * @param handleExpandCollapseArticle - Function to expand/collapse articles
 * @param treeData - Array of tree nodes
 */
export const useAutoExpandLoadingArticles = (
    articlesLoading: string[],
    expandedKeys: React.Key[],
    handleExpandCollapseArticle: (
        articleIdsToExpand: React.Key[],
        shouldExpand: boolean,
        articleNode: DataNode
    ) => void,
    treeData: DataNode[]
) => {
    const isMounted = useRef(true)

    const {
        keyMap,
        stableExpandedKeys,
        stableArticlesLoading,
    } = useMemo(() => {
        const keyMap = new Map<string, string>()

        // map node keys to stable keys
        const mapNodeKeys = (node: DataNode) => {
            const key = node.key as string
            const stableKey = `original-${key}`

            // store both directions for bidirectional lookup
            keyMap.set(key, stableKey)
            keyMap.set(stableKey, key)

            node.children?.forEach(mapNodeKeys)
        }

        treeData.forEach(mapNodeKeys)

        return {
            keyMap,
            stableExpandedKeys: expandedKeys.map(
                (key) => keyMap.get(key as string) || key
            ),
            stableArticlesLoading: articlesLoading.map(
                (key) => keyMap.get(key as string) || key
            ),
        }
    }, [treeData, expandedKeys, articlesLoading])

    // prevent updates after unmount (memory leaks)
    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    // Auto-expand articles that are loading
    useEffect(() => {
        // skip if no articles are loading or component unmounted
        if (!stableArticlesLoading.length || !isMounted.current) return

        // find articles to expand (exclude default and already expanded)
        const articlesToExpand = stableArticlesLoading.filter(
            (id) => id !== 'default' && !stableExpandedKeys.includes(id)
        )

        if (!articlesToExpand.length) return

        // stagger expansions with timeouts to prevent UI freezing
        const timeouts = articlesToExpand.map((articleKey, index) =>
            setTimeout(() => {
                if (!isMounted.current) return

                const originalIds = [
                    ...expandedKeys,
                    keyMap.get(articleKey) || articleKey,
                ]

                const node = ({
                    key: keyMap.get(articleKey) || articleKey,
                    children: [],
                } as unknown) as DataNode

                handleExpandCollapseArticle(originalIds, true, node)
            }, index * 50)
        )

        return () => timeouts.forEach(clearTimeout)
    }, [
        stableArticlesLoading,
        stableExpandedKeys,
        handleExpandCollapseArticle,
        keyMap,
        expandedKeys,
    ])
}
